import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { BarChart3, Download, Settings2 } from 'lucide-react';
import { centroAyudaService, type FeedbackAyudaDto } from '@/services/api/centroAyuda.service';
import { useArticulosAyuda } from '../hooks/useArticulosAyuda';

function descargarBlob(blob: Blob, nombreArchivo: string): void {
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = nombreArchivo;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  URL.revokeObjectURL(url);
}

interface FilaUtilidad {
  articuloId: string;
  categoria: string;
  titulo: string;
  votosUtil: number;
  votosNoUtil: number;
  tasa: number;
}

export function ReporteAyudaTab() {
  const navigate = useNavigate();
  const { articulos, cargando: cargandoArticulos } = useArticulosAyuda(false);
  const [feedback, setFeedback] = useState<FeedbackAyudaDto[]>([]);
  const [cargandoFeedback, setCargandoFeedback] = useState(true);
  const [descargando, setDescargando] = useState(false);

  useEffect(() => {
    void centroAyudaService.getFeedback().then((res) => {
      if (res.ok && res.data) setFeedback(res.data);
      setCargandoFeedback(false);
    });
  }, []);

  const filas: FilaUtilidad[] = articulos.map((a) => {
    const propios = feedback.filter((f) => f.articuloId === a.id);
    const votosUtil = propios.filter((f) => f.esUtil).length;
    const votosNoUtil = propios.length - votosUtil;
    const total = propios.length;
    return {
      articuloId: a.id,
      categoria: a.category,
      titulo: a.title,
      votosUtil,
      votosNoUtil,
      tasa: total > 0 ? Math.round((votosUtil / total) * 100) : 0,
    };
  });

  const handleDescargar = async () => {
    setDescargando(true);
    try {
      const blob = await centroAyudaService.descargarReporte();
      descargarBlob(blob, `centro_ayuda_utilidad_${new Date().toISOString().slice(0, 10)}.xlsx`);
    } finally {
      setDescargando(false);
    }
  };

  return (
    <div>
      <div className="flex items-center gap-3 mb-3">
        <div
          className="flex items-center justify-center shrink-0 bg-primary-light"
          style={{ width: 38, height: 38, borderRadius: 10 }}
        >
          <BarChart3 size={19} className="text-primary" />
        </div>
        <div>
          <p className="m-0 text-ui-sm font-semibold text-primary mb-1">Reporte de utilidad</p>
          <p className="m-0 text-caption text-tertiary">Solo visible para super administradores</p>
        </div>
      </div>

      <button
        type="button"
        onClick={() => navigate('/centro-ayuda/administrar')}
        className="mb-4 flex w-full items-center justify-center gap-1.5 rounded-md border border-default py-2 text-caption-medium text-secondary hover:bg-surface-hover"
      >
        <Settings2 className="h-3.5 w-3.5" />
        Administrar guías
      </button>

      {(cargandoArticulos || cargandoFeedback) ? (
        <p className="text-caption text-tertiary">Cargando...</p>
      ) : (
        <div className="mb-4 space-y-1.5">
          {filas.map((fila) => (
            <div key={fila.articuloId} className="rounded-md border border-default px-2.5 py-2">
              <p className="m-0 text-caption-medium text-primary truncate">{fila.titulo}</p>
              <p className="m-0 text-label text-tertiary">
                👍 {fila.votosUtil} · 👎 {fila.votosNoUtil} · {fila.tasa}% útil
              </p>
            </div>
          ))}
          {filas.length === 0 && (
            <p className="text-caption text-tertiary">Sin artículos registrados.</p>
          )}
        </div>
      )}

      <button
        type="button"
        onClick={() => void handleDescargar()}
        disabled={descargando}
        className="flex w-full items-center justify-center gap-1.5 rounded-md bg-brand py-2 text-caption-medium text-white disabled:opacity-60"
      >
        <Download className="h-3.5 w-3.5" />
        {descargando ? 'Generando...' : 'Descargar reporte'}
      </button>
    </div>
  );
}
